/**
 * Lightweight RUM probe. Activates when the URL contains `?perf=1`,
 * sessionStorage flag `perf_probe=1`, or you call `window.__perfNow()` manually.
 *
 * Prints a `console.table` shortly after load. Also exposes:
 *   window.__perf      — last collected snapshot
 *   window.__perfNow() — re-collect & print on demand (works even without ?perf=1)
 */

type PerfRow = { metric: string; value: string };

function flagEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('perf') === '1') {
      sessionStorage.setItem('perf_probe', '1');
      return true;
    }
    return sessionStorage.getItem('perf_probe') === '1';
  } catch {
    return false;
  }
}

function fmtMs(n: number | undefined): string {
  if (n === undefined || !isFinite(n)) return '—';
  return `${Math.round(n)} ms`;
}

function fmtKb(n: number | undefined): string {
  if (n === undefined || !isFinite(n)) return '—';
  return `${Math.round(n / 1024)} KB`;
}

function collect(label = 'auto') {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  const paints = performance.getEntriesByType('paint');
  const fcp = paints.find((p) => p.name === 'first-contentful-paint')?.startTime;
  const fp = paints.find((p) => p.name === 'first-paint')?.startTime;

  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const fetches = resources.filter(
    (r) => r.initiatorType === 'fetch' || r.initiatorType === 'xmlhttprequest'
  );
  const totalBytes = resources.reduce((s, r) => s + (r.transferSize || 0), 0);
  const slowest = [...resources].sort((a, b) => b.duration - a.duration)[0];

  let longestTask = 0;
  try {
    const tasks = performance.getEntriesByType('longtask');
    longestTask = tasks.reduce((m, t) => Math.max(m, t.duration), 0);
  } catch {
    /* longtask not supported */
  }

  const rows: PerfRow[] = [
    { metric: 'Route', value: window.location.pathname },
    { metric: 'TTFB', value: fmtMs(nav?.responseStart) },
    { metric: 'First Paint', value: fmtMs(fp) },
    { metric: 'FCP', value: fmtMs(fcp) },
    { metric: 'DOM Interactive', value: fmtMs(nav?.domInteractive) },
    { metric: 'DOM Content Loaded', value: fmtMs(nav?.domContentLoadedEventEnd) },
    { metric: 'Load Event', value: fmtMs(nav?.loadEventEnd) },
    { metric: 'Longest Task', value: fmtMs(longestTask) },
    { metric: 'Resource Count', value: String(resources.length) },
    { metric: 'Fetch/XHR Count', value: String(fetches.length) },
    { metric: 'Total Transfer', value: fmtKb(totalBytes) },
    {
      metric: 'Slowest Resource',
      value: slowest
        ? `${fmtMs(slowest.duration)} — ${slowest.name.split('/').pop()?.slice(0, 60) || slowest.name}`
        : '—',
    },
  ];

  /* eslint-disable no-console */
  console.log(
    `%c[perf:${label}] ${window.location.pathname}`,
    'color:#10b981;font-weight:bold;font-size:13px'
  );
  console.table(rows);
  console.log(
    'Top 5 slowest resources:',
    resources
      .slice()
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5)
      .map((r) => ({
        name: r.name.split('/').pop()?.slice(0, 80),
        duration_ms: Math.round(r.duration),
        size_kb: Math.round((r.transferSize || 0) / 1024),
      }))
  );
  /* eslint-enable no-console */

  (window as unknown as { __perf?: unknown }).__perf = { rows, resources, nav };
  return rows;
}

export function initPerfProbe() {
  if (typeof window === 'undefined') return;

  // Always expose the manual trigger so you can call __perfNow() in any session.
  (window as unknown as { __perfNow?: () => unknown }).__perfNow = () => collect('manual');

  if (!flagEnabled()) return;

  // eslint-disable-next-line no-console
  console.log('%c[perf] probe armed — will report after load', 'color:#10b981');

  const schedule = () => {
    // First snapshot once load fires + 1s for late chunks.
    setTimeout(() => collect('load+1s'), 1000);
    // Second snapshot at 4s to catch lazy/idle work.
    setTimeout(() => collect('load+4s'), 4000);
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}
